import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { LayoutDashboard, Briefcase, Users, ClipboardCheck, UploadCloud, FileCode2, LogOut, BarChart3, ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/positions", label: "Positions", icon: Briefcase },
  { to: "/candidates", label: "Candidates", icon: Users },
  { to: "/review", label: "Review Queue", icon: ClipboardCheck },
  { to: "/upload", label: "Upload Résumés", icon: UploadCloud },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/team", label: "Team", icon: ShieldCheck, adminOnly: true },
  { to: "/architecture", label: "Architecture", icon: FileCode2 },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 shrink-0 border-r border-border bg-secondary/40 flex flex-col sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-border">
          <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_tag-candidate-portal/artifacts/swk6w00s_IMG_9395.png" alt="The Allicock Group" className="h-9 w-auto object-contain" />
          <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--teal))] font-semibold mt-2.5">TAG Recruit AI</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map(({ to, label, icon: Icon }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-150 ${active
                  ? "bg-card border border-border shadow-card text-[hsl(var(--teal))] font-semibold"
                  : "border border-transparent text-foreground/65 hover:text-foreground hover:bg-card/70"}`}>
                <Icon size={17} className={active ? "text-[hsl(var(--teal))]" : "text-muted-foreground"} /> {label}
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="h-8 w-8 rounded-full bg-[hsl(var(--teal))] text-white flex items-center justify-center text-xs font-semibold shrink-0 font-head">
              {(user?.name || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate leading-tight">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate capitalize">{user?.role}</div>
            </div>
          </div>
          <div className="space-y-2">
            {user?.auth_provider !== "google" && <ChangePasswordDialog />}
            <button data-testid="logout-btn" onClick={doLogout}
              className="flex items-center gap-2 text-sm text-destructive hover:opacity-80 transition-opacity">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
